import { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';

import type { AiAction } from '~/components/AiChat/types';
import { isDynamicClaimActionTaskData } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';

/** The claim action tool the conductor moves a claim between stages with. */
const STAGE_CHANGE_TOOL_KEY = 'change_claim_stage';

interface StageChange {
  actionId: string;
  stageKey: string;
}

/**
 * When an action was executed, in epoch ms: read as UTC where the server's time carries no offset, as the log reads its
 * entries' times, so it compares with them and with the claim's stage history.
 */
export const executedAt = (action: AiAction): number =>
  moment.utc(action.status_change?.at ?? action.updated_at, moment.ISO_8601).valueOf();

/** The stage an executed stage change moved the claim to; `null` for any other action, or one not executed. */
export const executedStageChangeKey = (action: AiAction): string | null => {
  if (!action.is_executed || !isDynamicClaimActionTaskData(action.task_data)) return null;
  if (action.task_data.tool_key !== STAGE_CHANGE_TOOL_KEY) return null;

  const stageKey = action.task_data.envelope.payload.stage_key;

  return typeof stageKey === 'string' ? stageKey : null;
};

/** The stage change executed last, by when it was executed; `null` when none has been. */
export const latestExecutedStageChange = (actions: AiAction[]): StageChange | null => {
  let latest: StageChange | null = null;
  let latestAt = -Infinity;
  for (const action of actions) {
    const stageKey = executedStageChangeKey(action);
    const at = executedAt(action);
    if (stageKey !== null && at > latestAt) {
      latest = { actionId: action.action_id, stageKey };
      latestAt = at;
    }
  }

  return latest;
};

/**
 * Re-reads the claim when a stage change lands, so the stage bar and the page header move with the
 * conductor instead of staying at the stage the page was opened on, and the claim's stage history with
 * it, through `onStageChange`, so the log's stages rail gains the change.
 *
 * The claim stays the source of the stage: a handler can move it by hand after the conductor did,
 * and the action cannot know that. So an execution is only the signal to re-read. On the first read
 * the claim was fetched before the actions, and it is re-read only if the latest change disagrees
 * with it — the conductor may have moved the claim while the page sat on another tab.
 */
export const useRefreshClaimOnStageChange = (
  actions: AiAction[],
  isReady: boolean,
  onStageChange: () => void
): void => {
  const { claim, onClaimUpdate } = useClaim({ claimRequired: true });
  const seenActionId = useRef<string | null>();
  const latest = useMemo(() => latestExecutedStageChange(actions), [actions]);

  useEffect(() => {
    if (!isReady) return;

    const isFirstRead = seenActionId.current === undefined;
    const latestActionId = latest?.actionId ?? null;
    if (latestActionId === seenActionId.current) return;

    seenActionId.current = latestActionId;
    if (!latest || (isFirstRead && latest.stageKey === claim.stage_key)) return;

    onClaimUpdate();
    onStageChange();
  }, [isReady, latest, claim.stage_key, onClaimUpdate, onStageChange]);
};
